import React, { useState } from 'react';
import { Button, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FJ121 } from '../../../services/j202Api';

interface Tab2Props {
  details: Partial<FJ121>[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onDetailChange: (idx: number, key: keyof FJ121, value: FJ121[keyof FJ121]) => void;
}

const Tab2Details: React.FC<Tab2Props> = React.memo(({ details, isEditing, onAdd, onDelete, onDetailChange }) => {
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const selected = details[selectedIdx] || null;

  return (
    <div className="j202-tab2">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>繳庫明細 ({details.length} 筆)　總項數: {details.length}</span>
        {isEditing && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onAdd}>
            新增明細
          </Button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 33 }} data-contract-field="fj121_02">項次</th>
              <th style={{ width: 83 }} data-contract-field="fj121_10">產品代號1</th>
              <th style={{ width: 83 }} data-contract-field="fj121_11">產品代號2</th>
              <th style={{ width: 40 }} data-contract-field="fj121_12">色號</th>
              <th style={{ width: 47 }} data-contract-field="fj121_17">等級</th>
              <th style={{ width: 77 }} data-contract-field="fj121_13">繳庫數量</th>
              <th style={{ width: 42 }} data-contract-field="fj121_14">單位</th>
              <th style={{ width: 77 }} data-contract-field="fj121_15">單價</th>
              <th style={{ width: 69 }} data-contract-field="fj121_18">換算率</th>
              <th style={{ width: 93 }} data-contract-field="fj121_amount">金額</th>
              {isEditing && <th style={{ width: 50 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 12 : 11} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr
                key={idx}
                onClick={() => setSelectedIdx(idx)}
                style={{ cursor: 'pointer', background: idx === selectedIdx ? '#e6f7ff' : undefined }}
              >
                <td>{d.fj121_02 ?? (idx + 1)}</td>
                <td>
                  <input
                    type="text" maxLength={6}
                    disabled={!isEditing}
                    value={d.fj121_10 || ''}
                    onChange={e => onDetailChange(idx, 'fj121_10', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={6}
                    disabled={!isEditing}
                    value={d.fj121_11 || ''}
                    onChange={e => onDetailChange(idx, 'fj121_11', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={10}
                    disabled={!isEditing}
                    value={d.fj121_12 || ''}
                    onChange={e => onDetailChange(idx, 'fj121_12', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={10}
                    disabled={!isEditing}
                    value={d.fj121_17 || ''}
                    onChange={e => onDetailChange(idx, 'fj121_17', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.01"
                    disabled={!isEditing}
                    value={d.fj121_13 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj121_13', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={4}
                    disabled={!isEditing}
                    value={d.fj121_14 || ''}
                    onChange={e => onDetailChange(idx, 'fj121_14', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.001"
                    disabled={!isEditing}
                    value={d.fj121_15 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj121_15', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.0001"
                    disabled={!isEditing}
                    value={d.fj121_18 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj121_18', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.01"
                    disabled={true}
                    value={((d.fj121_13 ?? 0) * (d.fj121_15 ?? 0)).toFixed(2)}
                    style={{ width: '100%' }}
                  />
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="確定刪除此明細?" onConfirm={() => onDelete(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail area: shows fields for the selected grid row (FoxPro Page2 non-grid controls) */}
      {selected && (
        <div className="detail-area" style={{ marginTop: 16, border: '1px solid #e8e8e8', borderRadius: 4, padding: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#666' }}>
            明細第 {selectedIdx + 1} 項 — 附加資訊
          </div>
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: '6px 12px', alignItems: 'center' }}>
            <label data-contract-field="fj121_20">產品名稱</label>
            <textarea
              disabled={!isEditing}
              value={selected.fj121_20 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_20', e.target.value)}
              style={{ minHeight: 40, gridColumn: 'span 3' }}
            />

            <label data-contract-field="fj121_21">產品組織/規格</label>
            <textarea
              disabled={!isEditing}
              value={selected.fj121_21 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_21', e.target.value)}
              style={{ minHeight: 40, gridColumn: 'span 3' }}
            />

            <label data-contract-field="fj121_22">加工別</label>
            <textarea
              disabled={!isEditing}
              value={selected.fj121_22 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_22', e.target.value)}
              style={{ minHeight: 40, gridColumn: 'span 3' }}
            />

            <label data-contract-field="fj121_121">中文色名</label>
            <input type="text" readOnly value={selected.fj121_121 || ''} style={{ maxWidth: 200 }} />

            <label data-contract-field="fj121_122">英文色名</label>
            <input type="text" readOnly value={selected.fj121_122 || ''} style={{ maxWidth: 200 }} />

            <label data-contract-field="fj121_width">幅寬-針孔內/外</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number" step="0.1"
                disabled={!isEditing}
                value={selected.fj121_23 ?? 0}
                onChange={e => onDetailChange(selectedIdx, 'fj121_23', parseFloat(e.target.value) || 0)}
                style={{ width: 80 }}
              />
              <span>/</span>
              <input
                type="number" step="0.1"
                disabled={!isEditing}
                value={selected.fj121_24 ?? 0}
                onChange={e => onDetailChange(selectedIdx, 'fj121_24', parseFloat(e.target.value) || 0)}
                style={{ width: 80 }}
              />
            </div>

            <label data-contract-field="fj121_weight">重量-碼重/米平方重</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number" step="0.1"
                disabled={!isEditing}
                value={selected.fj121_25 ?? 0}
                onChange={e => onDetailChange(selectedIdx, 'fj121_25', parseFloat(e.target.value) || 0)}
                style={{ width: 80 }}
              />
              <span>/</span>
              <input
                type="number" step="0.1"
                disabled={!isEditing}
                value={selected.fj121_26 ?? 0}
                onChange={e => onDetailChange(selectedIdx, 'fj121_26', parseFloat(e.target.value) || 0)}
                style={{ width: 80 }}
              />
            </div>

            <label data-contract-field="fj121_27">印花配色</label>
            <input
              type="text" maxLength={30}
              disabled={!isEditing}
              value={selected.fj121_27 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_27', e.target.value)}
              style={{ maxWidth: 200 }}
            />

            <label data-contract-field="fj121_12a">CBNO.</label>
            <input
              type="text" maxLength={10}
              disabled={!isEditing}
              value={selected.fj121_12a || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_12a', e.target.value)}
              style={{ maxWidth: 120 }}
            />

            <label data-contract-field="fj121_board">板號</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="text" maxLength={4}
                disabled={!isEditing}
                value={selected.fj121_31 || ''}
                onChange={e => onDetailChange(selectedIdx, 'fj121_31' as keyof FJ121, e.target.value)}
                style={{ width: 80 }}
              />
              <span>/</span>
              <input
                type="text" maxLength={4}
                disabled={!isEditing}
                value={selected.fj121_32 || ''}
                onChange={e => onDetailChange(selectedIdx, 'fj121_32' as keyof FJ121, e.target.value)}
                style={{ width: 80 }}
              />
            </div>

            <label data-contract-field="fj121_35">架號</label>
            <input
              type="text" maxLength={4}
              disabled={!isEditing}
              value={selected.fj121_35 || ''}
              onChange={e => onDetailChange(selectedIdx, 'fj121_35' as keyof FJ121, e.target.value)}
              style={{ maxWidth: 80 }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

Tab2Details.displayName = 'Tab2Details';

export default Tab2Details;
